import React from 'react';
import { Select } from '@mantine/core';
import TabsBlock from './TabsBlock';
import TabsListField from './TabsListField';
import { tabPlainLabel } from './tabsLayout';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';
import { puckColorField } from '../../../puck/fields/createPuckColorField';
import { PxSpacingInput, puckPxSpacingField } from '../../../puck/fields/puckPxSpacingField';
import SectionPaddingYField from '../../proposal/ProposalSection/SectionPaddingYField';
import { PAGE_CONTENT_GUTTER_PX } from '../../../layout/pageLayoutConstants';
import { pxToRemCss } from '../../../utils/proposalUnits';
import { puckVisibilityField } from '../../../puck/fields/puckVisibilityField';
import AppearAnimationField, { puckAppearAnimationDurationField } from '../../../puck/fields/AppearAnimationField';
import { APPEAR_ANIMATION_DURATION_DEFAULT_MS } from '../../../utils/appearAnimations';
import { DEFAULT_SECTION_MARGIN_BOTTOM_CSS } from '../../../utils/proposalUnits';
import { puckAdvancedSectionField, puckSectionField, puckStyleGallerySectionField } from '../../../puck/fields/puckSectionField';
import { withThemedFieldValues } from '../../../puck/fields/themedFieldValues';
import { puckTextRolesField } from '../../../puck/fields/TextRolesField';
import { puckHorizontalAlignField } from '../../../puck/fields/puckHorizontalAlignField';
import {
  themedBlockProps, themedBlockResolveData, themedBlockResolveFields,
} from '../../../puck/themedBlocks';
import { styleDefaults } from '../../../../shared/theme/styleSchema';
import { TABS_STYLE_SCHEMA } from './tabsStyleSchema';
import { puckThemeStyleField } from '../../../puck/fields/ThemeStyleSelectField';

/**
 * Onglet posé au drop. Un seul : l'utilisateur ajoute les suivants depuis la
 * sidebar, et chacun arrive avec son Bloc (cf. `tabsData.js`).
 *
 * Id CONSTANT, et non `createTabId()` : `defaultProps` est sérialisé dans le
 * snapshot de `puckConfigStructure`, qu'un id horodaté casserait à chaque
 * exécution. Deux blocs Onglets partagent donc le même id d'onglet initial,
 * sans conséquence — la clé de zone est préfixée par l'id du BLOC
 * (`<blockId>:tab-<tabId>`), qui lui est unique.
 */
const INITIAL_TAB_ID = 't-initial';

/** Même gouttière qu'un Bloc : la zone à onglets s'aligne avec le reste. */
const DEFAULT_TABS_PADDING_X_CSS = pxToRemCss(PAGE_CONTENT_GUTTER_PX);

/** Les options ne sont connues qu'à l'exécution : elles listent les onglets. */
function DefaultTabField({ value, onChange }) {
  const { itemProps } = useSelectedItemProps();
  const tabs = itemProps.tabs || [];
  return (
    <Select
      size="xs"
      label="Onglet ouvert par défaut"
      wrapperProps={{ 'data-inspector-layout': 'stacked' }}
      data={[
        { value: '', label: 'Le premier de la liste' },
        ...tabs
          .filter((tab) => tab?.id)
          .map((tab, index) => ({ value: tab.id, label: tabPlainLabel(tab, index) })),
      ]}
      value={value || ''}
      onChange={(next) => onChange(next ?? '')}
      allowDeselect={false}
      comboboxProps={{ withinPortal: true }}
    />
  );
}

const tabsBlockConfig = {
  label: 'Onglets',
  defaultProps: {
    tabs: [{ id: INITIAL_TAB_ID, label: '<p>Premier onglet</p>', icon: '', iconColor: '' }],
    ...styleDefaults(TABS_STYLE_SCHEMA),
    listAlign: 'left',
    defaultTabId: '',
    // Réglages de proximité à la page, portés ici pour tous les onglets : les
    // Blocs imbriqués ne les affichent plus (cf. nestedSectionFields.js).
    appearAnimation: 'none',
    appearAnimationDuration: APPEAR_ANIMATION_DURATION_DEFAULT_MS,
    paddingTop: 0,
    paddingBottom: 0,
    paddingX: DEFAULT_TABS_PADDING_X_CSS,
    marginBottom: DEFAULT_SECTION_MARGIN_BOTTOM_CSS,
    isVisible: true,
  },
  // Le libellé d'un onglet s'édite sur le canvas, jamais ici (README §4).
  fields: withThemedFieldValues('tabs', {
    // Le bloc se pose à la racine : aucun Bloc parent ne porte sa visibilité.
    isVisible: puckVisibilityField(),

    _section1: puckSectionField('Contenu'),
    tabs: {
      type: 'custom',
      label: 'Onglets',
      render: (props) => <TabsListField {...props} />,
    },
    defaultTabId: {
      type: 'custom',
      label: 'Onglet ouvert par défaut',
      render: (props) => <DefaultTabField {...props} />,
    },
    // Une seule animation pour toute la zone à onglets : réglée par onglet,
    // elle se jouerait sans être vue (tous les panneaux sont montés d'un coup).

    _sectionGallery: puckStyleGallerySectionField('Onglets'),
    themeStyleId: puckThemeStyleField('tabs'),

    _section2: puckSectionField('Style'),
    variant: {
      type: 'select',
      label: 'Style de la barre',
      options: [
        { label: 'Onglets', value: 'default' },
        { label: 'Pilules', value: 'pills' },
      ],
    },
    accentColor: puckColorField("Couleur de l'onglet actif", {
      description: "Teinte du soulignement (ou de la pilule) de l'onglet ouvert.",
    }),

    _sectionText: puckSectionField('Typographie'),
    textStyles: puckTextRolesField([{ key: 'label', label: 'Libellés des onglets' }]),

    /* Mêmes marges qu'un Bloc, sous le même titre. */
    _section3: puckSectionField('Mise en page'),
    paddingTop: {
      type: 'custom',
      label: 'Marge verticale',
      render: (props) => <SectionPaddingYField {...props} />,
    },
    paddingX: {
      type: 'custom',
      label: 'Marge horizontale',
      render: ({ value, onChange }) => (
        <PxSpacingInput
          value={value === undefined ? DEFAULT_TABS_PADDING_X_CSS : value}
          onChange={onChange}
          label="Marge horizontale"
          description="0 = la zone à onglets prend toute la largeur de la page."
        />
      ),
    },
    marginBottom: puckPxSpacingField('Espace sous le bloc'),

    _section4: puckSectionField('Affichage'),
    listAlign: puckHorizontalAlignField('Alignement de la barre'),
    // « Couleur d'accent » ne disait pas ce qui était coloré.

    _section5: puckSectionField('Animation'),
    appearAnimation: {
      type: 'custom',
      label: "Animation d'apparition",
      render: ({ value, onChange }) => (
        <AppearAnimationField value={value} onChange={onChange} label="Animation d'apparition" />
      ),
    },

    _section6: puckAdvancedSectionField(),
    appearAnimationDuration: puckAppearAnimationDurationField(),
  }),
  // Suit son style du thème, s'il en a un, retouches du thème comprises
  // (lien vivant, cf. puck/themedBlocks).
  resolveData: themedBlockResolveData('tabs'),
  resolveFields: themedBlockResolveFields('tabs'),
  render: (props) => <TabsBlock {...themedBlockProps('tabs', props, props.puck?.metadata)} />,
};

export default tabsBlockConfig;
